<script setup>
import { ref, watch, onMounted, defineProps, defineEmits } from 'vue';
import { useToast } from 'vue-toast-notification';
import vSelect from 'vue-select';
import apiClient from '@/services/api';

// Initialize toast
const toast = useToast();

// Props
const props = defineProps({
  formData: {
    type: Object,
    required: true,
  },
});

// Emits
const emit = defineEmits(['update:formData']);

// Reactive state
// Card added as a prepayment method alongside Cash and Mpesa
const paymentMethods = ref(['Cash', 'Mpesa', 'Card', 'FFS', 'Insurance']);
const selectedPaymentMethod = ref(null);
const showLoader = ref(false);
const patientRegForm = ref({
  ...props.formData,
  patient: {
    ...props.formData.patient,
    schemes: Array.isArray(props.formData.patient.schemes) ? props.formData.patient.schemes : [],
  },
});
const patientSchemes = ref(patientRegForm.value.patient.schemes);
const errors = ref({});

// MTIBA Form Data
const insuranceSchemes = ref([
  { id: 'mtiba', name: 'M-TIBA' },
  { id: 'sha', name: 'SHA' }
]);
const insuranceType = ref('mtiba'); // 'mtiba' or 'sha'
const mtibaForm = ref({
  insurer: null,
  visit_code: '',
});
const mtibaBeneficiaryData = ref(null);
const mtibaVerified = ref(false);

// SHA Data
const shaForm = ref({
  id_number: '',
  id_type: 'National ID',
  otp: '',
  cr_number: '',
  eligibilityData: null,
  shaVisitNumber: '',
  consent_token: '',
  intervention_code: 'PMF-12-001',
  member_name: '',
});
const shaVerified = ref(false);
const shaVisitClaimId = ref(null);

// FFS Form Data
const companies = ref([]);
const ffsForm = ref({
  company: null,
  id_number: '',
});
const beneficiary = ref(null);
const beneficiaryFound = ref(false);

// Methods
const fetchCompanies = async () => {
  try {
    showLoader.value = true;
    const response = await apiClient.searchCompanies({});
    if (response.success) {
      companies.value = response.data.map(company => ({
        id: company.id,
        name: company.name,
      }));
    } else {
      toast.error(response.message || 'Failed to fetch companies.');
    }
  } catch (error) {
    toast.error('Error fetching companies: ' + error.message);
  } finally {
    showLoader.value = false;
  }
};

const selectPaymentMethod = (method) => {
  selectedPaymentMethod.value = method;
  resetForms();
  if (method === 'FFS') {
    if (!companies.value.length) {
      fetchCompanies();
    }
  } else if (['Cash', 'Mpesa', 'Card'].includes(method)) {
    // Card is a prepayment method — no extra form needed, just confirm and add
    toast.success(`${method} selected. Click 'Add' to confirm.`);
  } else if (method === 'Insurance') {
    insuranceType.value = 'mtiba';
    mtibaForm.value.insurer = insuranceSchemes.value.find(s => s.id === 'mtiba');
  }
};

const validateInsuranceForm = () => {
  errors.value = {};
  let isValid = true;
  if (!mtibaForm.value.visit_code.trim()) {
    errors.value.visit_code = 'Visit code is required.';
    isValid = false;
  }
  return isValid;
};

const validateFFSForm = () => {
  errors.value = {};
  let isValid = true;
  if (!ffsForm.value.company) {
    errors.value.company = 'Please select a company.';
    isValid = false;
  }
  if (!ffsForm.value.id_number.trim()) {
    errors.value.id_number = 'Beneficiary ID number is required.';
    isValid = false;
  }
  return isValid;
};

const addPatientScheme = () => {
  // Card is treated identically to Cash and Mpesa — a simple prepayment with no extra details.
  // The backend whitelist already includes 'card' in %w[cash mpesa airtel_money card].
  if (['Cash', 'Mpesa', 'Card'].includes(selectedPaymentMethod.value)) {
    if (!patientSchemes.value.some(s => s.payment_method === selectedPaymentMethod.value)) {
      patientSchemes.value.push({ payment_method: selectedPaymentMethod.value, details: {} });
      updateParentForm();
      toast.success(`${selectedPaymentMethod.value} added successfully.`);
    } else {
      toast.error(`${selectedPaymentMethod.value} is already added.`);
      return;
    }
  } else if (selectedPaymentMethod.value === 'Insurance') {
    if (!validateInsuranceForm()) {
      toast.error('Please address the errors in the insurance form.');
      return;
    }
    patientSchemes.value.push({
      payment_method: 'Insurance',
      details: {
        insurance_id: 'MTIBA001', // MTIBA insurance code
        visit_code: mtibaForm.value.visit_code,
        beneficiary_data: mtibaBeneficiaryData.value,
      },
    });
    resetMtibaForm();
    updateParentForm();
    toast.success('Insurance scheme added successfully.');
  } else if (selectedPaymentMethod.value === 'FFS' && beneficiary.value) {
    if (!validateFFSForm()) {
      toast.error('Please address the errors in the FFS form.');
      return;
    }
    patientSchemes.value.push({
      payment_method: 'FFS',
      details: {
        company_id: ffsForm.value.company.id,
        company_name: ffsForm.value.company.name,
        beneficiary_id: beneficiary.value.id,
        beneficiary_name: beneficiary.value.name,
        id_number: beneficiary.value.id_number,
      },
    });
    resetFFSForm();
    updateParentForm();
    toast.success('FFS scheme added successfully.');
  } else {
    toast.error('No valid payment method selected or beneficiary not found.');
    return;
  }
};

const removePatientScheme = async (index) => {
  const scheme = patientSchemes.value[index];

  // If it's an insurance scheme with a reservation/claim, delete it
  if (scheme.payment_method === 'Insurance') {
    if (scheme.details.reservation_id) {
      try {
        showLoader.value = true;
        const response = await apiClient.deleteMtibaReservation(scheme.details.reservation_id);
        if (!response.success) {
          toast.error('Failed to delete reservation: ' + response.message);
          return;
        }
      } catch (error) {
        toast.error('Error deleting reservation: ' + error.message);
        return;
      } finally {
        showLoader.value = false;
      }
    }
    if (scheme.details.sha_visit_claim_id) {
      try {
        showLoader.value = true;
        const response = await apiClient.closeShaClaim(
          scheme.details.visit_code || scheme.details.sha_visit_number
        );
        if (!response.success) {
          toast.error('Failed to close SHA claim: ' + response.message);
          return;
        }
      } catch (error) {
        toast.error('Error closing SHA claim: ' + error.message);
        return;
      } finally {
        showLoader.value = false;
      }
    }
  }

  patientSchemes.value.splice(index, 1);
  updateParentForm();
  toast.success('Payment method removed successfully.');
};

// Legacy function - kept for potential future use
// eslint-disable-next-line no-unused-vars
const resetInsuranceForm = () => {
  // Legacy implementation
};

const resetFFSForm = () => {
  ffsForm.value = { company: null, id_number: '' };
  beneficiary.value = null;
  beneficiaryFound.value = false;
  errors.value = {};
};

const resetForms = () => {
  resetMtibaForm();
  resetFFSForm();
  resetMtibaForm();
};

const updateParentForm = () => {
  patientRegForm.value.patient.schemes = patientSchemes.value;
  emit('update:formData', patientRegForm.value);
};

// Legacy functions - kept for potential future use but currently unused
// eslint-disable-next-line no-unused-vars
const searchInsurance = async (search) => {
  // Legacy implementation - not currently used
};

// eslint-disable-next-line no-unused-vars
const fetchInsuranceSchemes = async (insurance) => {
  // Legacy implementation - not currently used
};

const searchBeneficiary = async () => {
  if (!validateFFSForm()) {
    toast.error('Please address the errors in the FFS form.');
    return;
  }
  try {
    showLoader.value = true;
    const payload = {
      company_id: ffsForm.value.company.id,
      id_number: ffsForm.value.id_number,
    };
    const response = await apiClient.searchBeneficiary(payload);
    if (response.success) {
      beneficiary.value = response.data;
      beneficiaryFound.value = true;
      toast.success('Beneficiary found: ' + response.data.name);
    } else {
      beneficiary.value = null;
      beneficiaryFound.value = false;
      toast.error(response.message || 'Beneficiary not found.');
    }
  } catch (error) {
    beneficiary.value = null;
    beneficiaryFound.value = false;
    toast.error('Error searching beneficiary: ' + error.message);
  } finally {
    showLoader.value = false;
  }
};

const searchMtibaBeneficiary = async () => {
  if (!mtibaForm.value.visit_code.trim()) {
    toast.error('Please enter a visit code.');
    return;
  }
  try {
    showLoader.value = true;
    const response = await apiClient.fetchMtibaBeneficiaryData({ visit_code: mtibaForm.value.visit_code });
    if (response.success) {
      // Map the API response to match the expected structure
      const apiData = response.data;
      mtibaBeneficiaryData.value = {
        visit_code: apiData.visitCode,
        beneficiary_name: apiData.accountHolder?.fullName,
        date_of_birth: apiData.accountHolder?.dateOfBirth,
        gender: apiData.accountHolder?.gender,
        mobile_number: apiData.accountHolder?.mobileNumber,
        relationship: apiData.accountHolder?.relationship,
        membership_number: apiData.accountHolder?.membershipNumber,
        policy_number: apiData.account?.policyNumber,
        payer_name: apiData.account?.payerName,
        plan: apiData.account?.plan,
        status: apiData.account?.status,
        currency: apiData.account?.limit?.currency,
        amount: apiData.account?.limit?.amount,
        patient: apiData.patient ? {
          fullName: apiData.patient.fullName,
          dateOfBirth: apiData.patient.dateOfBirth,
          gender: apiData.patient.gender,
          mobileNumber: apiData.patient.mobileNumber,
          relationship: apiData.patient.relationship
        } : null,
        copaymentAmount: apiData.copaymentAmount ? {
          currency: apiData.copaymentAmount.currency,
          amount: apiData.copaymentAmount.amount
        } : null
      };
      mtibaVerified.value = true;
      toast.success('MTIBA beneficiary data retrieved successfully.');
    } else {
      mtibaBeneficiaryData.value = null;
      mtibaVerified.value = false;
      toast.error(response.message || 'Failed to retrieve MTIBA beneficiary data.');
    }
  } catch (error) {
    mtibaBeneficiaryData.value = null;
    mtibaVerified.value = false;
    toast.error('Error retrieving MTIBA beneficiary data: ' + error.message);
  } finally {
    showLoader.value = false;
  }
};

const proceedWithMtiba = async () => {
  if (!mtibaVerified.value || !mtibaBeneficiaryData.value) {
    toast.error('Please search for beneficiary data first.');
    return;
  }

  try {
    showLoader.value = true;
    // Create reservation in backend
    const reservationResponse = await apiClient.createMtibaReservation({
      visit_code: mtibaForm.value.visit_code
      // visit_id will be set later after visit creation
    });

    if (reservationResponse.success) {
      patientSchemes.value.push({
        payment_method: 'Insurance',
        details: {
          insurance_id: 'MTIBA001', // MTIBA insurance code
          visit_code: mtibaForm.value.visit_code,
          beneficiary_data: mtibaBeneficiaryData.value,
          reservation_id: reservationResponse.data.id, // Store reservation ID
        },
      });
      resetMtibaForm();
      updateParentForm();
      toast.success('MTIBA insurance added successfully.');
    } else {
      toast.error(reservationResponse.message || 'Failed to create MTIBA reservation.');
    }
  } catch (error) {
    toast.error('Error creating MTIBA reservation: ' + error.message);
  } finally {
    showLoader.value = false;
  }
};

const resetMtibaForm = () => {
  mtibaForm.value = { insurer: insuranceSchemes.value[0], visit_code: '' };
  mtibaBeneficiaryData.value = null;
  mtibaVerified.value = false;
};

// SHA Functions
const checkShaEligibility = async () => {
  if (!shaForm.value.id_number.trim()) {
    toast.error('Please enter ID number.');
    return;
  }
  try {
    showLoader.value = true;
    const response = await apiClient.checkShaEligibility({
      id_number: shaForm.value.id_number,
      id_type: shaForm.value.id_type,
    });
    if (response.success) {
      shaForm.value.eligibilityData = response.data;
      shaForm.value.cr_number = response.data.cr_number || response.data.memberCrNumber || response.data.member_number;
      shaVerified.value = true;
      toast.success('SHA eligibility verified. Copay: ' + (response.data.copay?.amount || '0'));

      // Fetch patient sub-benefits and interventions, then auto-send OTP
      if (shaForm.value.cr_number) {
        try {
          // Step 1: Get patient's sub-benefits
          const subBenefitsRes = await apiClient.getShaPatientSubBenefits(shaForm.value.cr_number);
          const subBenefits = subBenefitsRes.success ? (subBenefitsRes.data.results || subBenefitsRes.data || []) : [];
          const subBenefitCode = subBenefits.length > 0 ? (subBenefits[0].subBenefitCode || subBenefits[0].code || subBenefits[0].sub_benefit_code) : 'SHA-12';
          console.log('SHA sub-benefits:', subBenefits, 'selected:', subBenefitCode);

          // Step 2: Get interventions for the sub-benefit
          const interventionsRes = await apiClient.getShaPatientInterventions(shaForm.value.cr_number, subBenefitCode);
          const interventions = interventionsRes.success ? (interventionsRes.data.results || interventionsRes.data || []) : [];
          console.log('SHA interventions:', interventions);
          const interventionCode = interventions.length > 0 ? (interventions[0].interventionCode || interventions[0].code) : null;

          if (interventionCode) {
            // Step 3: Send OTP with valid intervention code
            const otpResponse = await apiClient.sendShaOtp({
              patient_id: shaForm.value.cr_number,
              intervention_codes: [interventionCode],
            });
            if (otpResponse.success) {
              shaForm.value.intervention_code = interventionCode;
              toast.success('OTP sent successfully. Enter it below to start the visit.');
            } else {
              toast.error(otpResponse.message || 'Failed to send OTP.');
            }
          } else {
            toast.error('No valid interventions found for this patient. Cannot send OTP.');
          }
        } catch (otpError) {
          toast.error('Error sending OTP: ' + otpError.message);
        }
      }
    } else {
      shaForm.value.eligibilityData = null;
      shaVerified.value = false;
      toast.error(response.message || 'SHA eligibility check failed.');
    }
  } catch (error) {
    shaForm.value.eligibilityData = null;
    shaVerified.value = false;
    toast.error('Error checking SHA eligibility: ' + error.message);
  } finally {
    showLoader.value = false;
  }
};

const startShaVisitConsent = async () => {
  if (!shaVerified.value || !shaForm.value.eligibilityData) {
    toast.error('Please verify SHA eligibility first.');
    return;
  }
  if (!shaForm.value.otp.trim()) {
    toast.error('Please enter OTP.');
    return;
  }
  try {
    showLoader.value = true;
    const response = await apiClient.startShaVisitConsent({
      otp: shaForm.value.otp,
      patient_id: shaForm.value.cr_number,
      service_type: 'OUTPATIENT',
      intervention_codes: [shaForm.value.intervention_code || 'PMF-12-001'],
    });
    if (response.success) {
      shaVisitClaimId.value = response.data.id || response.data.claim_id;
      shaForm.value.shaVisitNumber = response.data.sha_visit_number;
      shaForm.value.consent_token = response.data.consent_token;
      shaForm.value.member_name = response.data.sha_response_data?.patient_name || response.data.member_name || '';
      toast.success('SHA visit consent started.');
    } else {
      toast.error(response.message || 'Failed to start SHA visit consent.');
    }
  } catch (error) {
    toast.error('Error starting SHA visit consent: ' + error.message);
  } finally {
    showLoader.value = false;
  }
};

const proceedWithSha = () => {
  if (!shaVerified.value || !shaVisitClaimId.value) {
    toast.error('Please complete SHA eligibility and consent.');
    return;
  }
  patientSchemes.value.push({
    payment_method: 'Insurance',
    details: {
      insurance_id: 'SHA001',
      sha_visit_claim_id: shaVisitClaimId.value,
      cr_number: shaForm.value.cr_number,
      eligibility_data: shaForm.value.eligibilityData,
       visit_code: shaForm.value.shaVisitNumber,
        member_name: shaForm.value.member_name,
    },
  });
  resetShaForm();
  updateParentForm();
  toast.success('SHA insurance added successfully.');
};

const resetShaForm = () => {
  shaForm.value = {
    id_number: '',
    id_type: 'National ID',
    otp: '',
    cr_number: '',
    eligibilityData: null,
    shaVisitNumber: '',
    consent_token: '',
    member_name: '',
  };
  shaVerified.value = false;
   shaVisitClaimId.value = null;
 };

 // Helper method to format date strings
 const formatDate = (dateStr) => {
   if (!dateStr) return 'N/A';
   return new Date(dateStr).toLocaleDateString('en-US', {
     year: 'numeric',
     month: 'short',
     day: 'numeric'
   });
 };

 // Watchers
 watch(() => props.formData, (newFormData) => {
  patientRegForm.value = {
    ...newFormData,
    patient: {
      ...newFormData.patient,
      schemes: Array.isArray(newFormData.patient.schemes) ? newFormData.patient.schemes : [],
    },
  };
  patientSchemes.value = patientRegForm.value.patient.schemes;
}, { deep: true });

// Watch for insurance type switches
watch(insuranceType, (newType) => {
  if (newType === 'mtiba') {
    mtibaForm.value.insurer = insuranceSchemes.value.find(s => s.id === 'mtiba');
    resetShaForm();
  } else if (newType === 'sha') {
    resetMtibaForm();
  }
});

// Initialize companies
onMounted(() => {
  fetchCompanies();
});
</script>

<template>
  <div class="flex flex-col w-full max-w-4xl mx-auto">
    <!-- Payment Method Selection -->
    <!-- grid-cols-5 to accommodate the 5 options: Cash, Mpesa, Card, FFS, Insurance -->
    <div class="grid grid-cols-5 gap-4 mb-6">
      <button v-for="method in paymentMethods" :key="method" type="button" :class="[
        'px-4 py-2 rounded-lg text-white font-medium',
        selectedPaymentMethod === method ? 'bg-blue-700' : 'bg-gray-400 hover:bg-gray-500'
      ]" @click="selectPaymentMethod(method)">
        {{ method }}
      </button>
    </div>

    <!-- Cash / Mpesa / Card Confirmation -->
    <!-- Card is a prepayment method with no extra form — same UX as Cash and Mpesa -->
    <div v-if="['Cash', 'Mpesa', 'Card'].includes(selectedPaymentMethod)" class="space-y-4">
      <p class="text-gray-600">Selected payment method: {{ selectedPaymentMethod }}</p>
      <div class="flex space-x-4">
        <button type="button" class="px-4 py-2 text-white bg-blue-700 rounded-lg hover:bg-blue-600"
          @click="addPatientScheme">
          Add
        </button>
        <button type="button" class="px-4 py-2 text-gray-800 bg-gray-200 rounded-lg hover:bg-gray-300"
          @click="resetForms">
          Cancel
        </button>
      </div>
    </div>

    <!-- Insurance Form -->
    <div v-if="selectedPaymentMethod === 'Insurance'" class="space-y-6">
      <!-- Insurance Provider Tabs -->
      <div>
        <label class="block mb-2 text-sm font-medium text-gray-700">Insurance Provider <span
            class="text-red-500">*</span></label>
        <div class="flex border-b border-gray-200">
          <button type="button" @click="insuranceType = 'mtiba'" :class="[
            'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2',
            insuranceType === 'mtiba'
              ? 'border-blue-500 text-blue-600 bg-blue-50'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          ]">
            M-TIBA
          </button>
          <button type="button" @click="insuranceType = 'sha'" :class="[
            'flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center justify-center gap-2',
            insuranceType === 'sha'
              ? 'border-orange-500 text-orange-600 bg-orange-50'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          ]">
            SHA
          </button>
        </div>
        <p v-if="errors.insurer" class="mt-1 text-sm text-red-500">{{ errors.insurer }}</p>
      </div>

      <!-- M-TIBA Form Section -->
      <div v-if="insuranceType === 'mtiba'" class="p-6 space-y-4 bg-white border border-gray-200 rounded-lg">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label class="block mb-1 text-sm font-medium text-gray-700">M-TIBA Visit Code <span
                class="text-red-500">*</span></label>
            <input v-model="mtibaForm.visit_code" type="text"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter visit code provided by facility" />
          </div>
          <div class="flex items-end">
            <button type="button" :disabled="showLoader || !mtibaForm.visit_code.trim()"
              class="flex items-center w-full gap-2 px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 md:w-auto"
              @click="searchMtibaBeneficiary">
              <svg v-if="!showLoader" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <svg v-else class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none"
                viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              </svg>
              <span>{{ showLoader ? 'Searching...' : 'Search Beneficiary' }}</span>
            </button>
          </div>
        </div>

        <!-- M-TIBA Beneficiary Verification -->
        <div v-if="insuranceType === 'mtiba' && mtibaVerified && mtibaBeneficiaryData"
          class="mt-6 overflow-hidden bg-white border-2 border-blue-200 shadow-sm rounded-xl">
          <div class="flex items-center justify-between mb-6">
            <h3 class="flex items-center text-xl font-bold text-gray-800">
              <svg class="w-6 h-6 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Beneficiary Information Verified
            </h3>
            <div class="flex items-center text-green-600">
              <svg class="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"></path>
              </svg>
              <span class="text-sm font-medium">Valid Coverage</span>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <!-- Account Holder Card -->
            <div class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
              <div class="flex items-center mb-3">
                <h4 class="font-semibold text-gray-800">Account Holder</h4>
              </div>
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Name:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.beneficiary_name }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Date of Birth:</span>
                  <span class="font-medium text-gray-900">{{ new
                    Date(mtibaBeneficiaryData.date_of_birth).toLocaleDateString() }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Gender:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.gender }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Mobile:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.mobile_number }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Relationship:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.relationship }}</span>
                </div>
                <div class="flex justify-between" v-if="mtibaBeneficiaryData.membership_number">
                  <span class="text-sm text-gray-600">Membership #:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.membership_number }}</span>
                </div>
              </div>
            </div>

            <!-- Account Information Card -->
            <div class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
              <div class="flex items-center mb-3">
                <h4 class="font-semibold text-gray-800">Account Information</h4>
              </div>
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Plan:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.plan }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Policy Number:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.policy_number }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Payer:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.payer_name }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Status:</span>
                  <span class="font-medium text-gray-900">
                    <span :class="mtibaBeneficiaryData.status === 'ACTIVE' ? 'text-green-600' : 'text-yellow-600'">
                      {{ mtibaBeneficiaryData.status }}
                    </span>
                  </span>
                </div>
                <div class="flex items-center justify-between pt-2 border-t">
                  <span class="text-sm font-medium text-gray-600">Coverage Limit:</span>
                  <span class="text-lg font-bold text-green-600">
                    {{ mtibaBeneficiaryData.currency }} {{ mtibaBeneficiaryData.amount?.toLocaleString() }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Patient Information Card (if different from account holder) -->
            <div class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm" v-if="mtibaBeneficiaryData.patient">
              <div class="flex items-center mb-3">
                <h4 class="font-semibold text-gray-800">Patient Information</h4>
              </div>
              <div class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Name:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.patient.fullName }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Date of Birth:</span>
                  <span class="font-medium text-gray-900">{{ new
                    Date(mtibaBeneficiaryData.patient.dateOfBirth).toLocaleDateString() }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Gender:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.patient.gender }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Mobile:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.patient.mobileNumber }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Relationship:</span>
                  <span class="font-medium text-gray-900">{{ mtibaBeneficiaryData.patient.relationship }}</span>
                </div>
              </div>
            </div>

            <!-- Copayment Information Card -->
            <div class="p-4 bg-white border border-gray-200 rounded-lg shadow-sm"
              v-if="mtibaBeneficiaryData.copaymentAmount">
              <div class="flex items-center mb-3">
                <div class="flex items-center justify-center w-8 h-8 mr-3 bg-orange-100 rounded-full">
                  <svg class="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1">
                    </path>
                  </svg>
                </div>
                <h4 class="font-semibold text-gray-800">Copayment Required</h4>
              </div>
              <div class="space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600">Copayment Amount:</span>
                  <span class="text-lg font-bold text-orange-600">
                    {{ mtibaBeneficiaryData.copaymentAmount.currency }} {{
                      mtibaBeneficiaryData.copaymentAmount.amount?.toLocaleString() }}
                  </span>
                </div>
                <div class="p-3 mt-3 rounded-md bg-orange-50">
                  <p class="text-xs text-orange-800">
                    <strong>Note:</strong> This amount will be charged to the patient before insurance coverage applies.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Visit Code Summary -->
          <div class="p-4 mt-6 border border-gray-200 rounded-lg">
            <div class="flex items-center justify-between">
              <div>
                <h5 class="font-medium text-blue-900">Visit Code Verified</h5>
                <p class="text-sm text-gray-800">{{ mtibaForm.visit_code }}</p>
              </div>
              <div class="text-right">
                <p class="text-xs text-blue-900">Insurance Provider</p>
                <p class="font-medium text-gray-800">M-TIBA</p>
              </div>
            </div>
          </div>

          <div class="flex justify-between mt-6 space-x-4">
            <button type="button"
              class="flex items-center px-6 py-3 font-medium text-white transition-colors duration-200 bg-green-600 rounded-lg shadow-sm hover:bg-green-700"
              @click="proceedWithMtiba">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Proceed with Insurance
            </button>
            <button type="button"
              class="px-6 py-3 font-medium text-gray-800 transition-colors duration-200 bg-gray-200 rounded-lg shadow-sm hover:bg-gray-300"
              @click="resetMtibaForm">
              Search Different Code
            </button>
           </div>
         </div>
       </div><div v-else-if="insuranceType === 'sha'" class="space-y-4">
         <!-- Step 1: Eligibility Check -->
        <div class="p-6 bg-white border border-gray-200 rounded-lg">
          <div v-if="!shaVerified" class="space-y-4">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label class="block text-sm font-medium text-gray-700">ID Type <span
                    class="text-red-500">*</span></label>
                  <select v-model="shaForm.id_type"
                    class="w-full px-3 py-2 mt-1 text-sm border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500">
                    <option value="National ID">National ID</option>
                    <option value="Passport">Passport</option>
                    <option value="Birth Certificate">Birth Certificate</option>
                    <option value="Alien ID">Alien ID</option>
                    <option value="Mandate Number">Mandate Number</option>
                    <option value="Temporary ID">Temporary ID</option>
                    <option value="SHA Number">SHA Number</option>
                    <option value="Refugee ID">Refugee ID</option>
                  </select>
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700">ID Number <span
                    class="text-red-500">*</span></label>
                <input v-model="shaForm.id_number" type="text"
                  class="w-full px-3 py-2 mt-1 text-sm border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Enter ID Number" />
              </div>
            </div>
            <div class="flex justify-end">
              <button type="button" :disabled="showLoader || !shaForm.id_number.trim()"
                class="flex items-center gap-2 px-6 py-2 text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
                @click="checkShaEligibility">
                <svg v-if="!showLoader" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <svg v-else class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none"
                  viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                </svg>
                <span>{{ showLoader ? 'Checking...' : 'Check Eligibility' }}</span>
              </button>
            </div>
          </div>

          <!-- Eligibility Results -->
          <div v-if="shaVerified && shaForm.eligibilityData" class="pt-6 mt-6 border-t border-gray-200">
            <div class="flex items-center gap-2 mb-4">
              <div class="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
                <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd" />
                </svg>
              </div>
              <div>
                <h5 class="font-semibold text-gray-800">Eligibility Confirmed</h5>
                <p class="text-sm text-gray-500">Proceed to verify with OTP</p>
              </div>
            </div>

            <!-- Member Information -->
            <div class="p-4 mb-4 bg-white border border-gray-200 rounded-lg">
              <h5 class="mb-3 font-semibold text-gray-800">Member Information</h5>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <span class="block text-xs text-gray-500">Member Name</span>
                  <span class="font-medium text-gray-900">{{ shaForm.eligibilityData.fullName }}</span>
                </div>
                <div>
                  <span class="block text-xs text-gray-500">CR Number</span>
                  <span class="font-mono font-medium text-gray-900">{{ shaForm.eligibilityData.memberCrNumber }}</span>
                </div>
              </div>
            </div>

            <!-- Schemes / Plans -->
            <div v-if="shaForm.eligibilityData.schemes?.length" class="p-4 mb-4 bg-white border border-gray-200 rounded-lg">
              <h5 class="mb-3 font-semibold text-gray-800">Active Schemes</h5>
              <div class="space-y-3">
                <div v-for="(scheme, index) in shaForm.eligibilityData.schemes" :key="index"
                     class="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                  <div>
                    <div class="font-medium text-gray-800">{{ scheme.schemeName }}</div>
                    <div class="text-xs text-gray-500">Policy: {{ scheme.policy?.number }}</div>
                    <div class="text-xs text-gray-500">
                      Coverage: {{ formatDate(scheme.coverage?.startDate) }} - {{ formatDate(scheme.coverage?.endDate) }}
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="text-sm font-medium" :class="scheme.coverage?.status === '1' ? 'text-green-600' : 'text-yellow-600'">
                      {{ scheme.coverage?.status === '1' ? 'Active' : scheme.coverage?.message || 'Inactive' }}
                    </div>
                    <div v-if="scheme.coverage?.reason" class="text-xs text-gray-500">
                      {{ scheme.coverage.reason }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Copayment Section -->
            <div v-if="shaForm.eligibilityData.copay" class="p-4 mb-4 border border-orange-200 rounded-lg bg-orange-50">
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd" />
                </svg>
                <span class="font-medium text-orange-800">Copayment Required</span>
              </div>
              <p class="mt-1 text-sm text-orange-700">
                Amount: <span class="font-bold">
                  {{ shaForm.eligibilityData.copay.currency }}
                  {{ shaForm.eligibilityData.copay.amount?.toLocaleString() }}
                </span>
              </p>
            </div>

            <!-- OTP Section -->
            <div class="space-y-4">
              <div>
                <label class="block mb-1 text-sm font-medium text-gray-700">Enter OTP <span
                    class="text-red-500">*</span></label>
                <input v-model="shaForm.otp" type="text" maxlength="6"
                  class="w-40 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                  placeholder="6-digit OTP" />
                <p class="mt-1 text-xs text-gray-500">Enter the OTP sent to the member's registered phone</p>
              </div>
              <div class="flex gap-3">
                <button type="button" :disabled="showLoader || !shaForm.otp.trim()"
                  class="flex items-center gap-2 px-6 py-2 text-white bg-orange-600 rounded-lg hover:bg-orange-700 disabled:opacity-50"
                  @click="startShaVisitConsent">
                  <svg v-if="!showLoader" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <svg v-else class="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  </svg>
                  <span>Confirm & Start Visit</span>
                </button>
                <button type="button" class="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  @click="resetShaForm">
                  Cancel
                </button>
              </div>
            </div>

            <!-- Success: Add Button -->
            <div v-if="shaVisitClaimId" class="p-6 pt-6 mt-6 -mx-6 -mb-6 border-t border-green-200 bg-green-50">
              <div class="flex items-center gap-2 mb-3">
                <div class="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
                  <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clip-rule="evenodd" />
                  </svg>
                </div>
                <span class="font-semibold text-green-800">Visit consent confirmed</span>
              </div>
              <p class="mb-4 text-sm text-green-700">
                SHA claim created. Visit number: <span class="font-mono font-medium">{{ shaForm.shaVisitNumber }}</span>.
                Click below to add SHA coverage to this patient.
              </p>
              <button type="button" :disabled="showLoader"
                class="flex items-center gap-2 px-6 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
                @click="proceedWithSha">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>Add SHA Coverage</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FFS Form -->
    <div v-if="selectedPaymentMethod === 'FFS'" class="space-y-4">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label for="company" class="block text-sm font-medium text-gray-700">FFS Company <span
              class="text-red-500">*</span></label>
          <v-select id="company" v-model="ffsForm.company" :options="companies" :searchable="true" :loading="showLoader"
            label="name" placeholder="Select Company..." :filterable="true" />
          <span v-if="errors.company" class="text-sm text-red-500">{{ errors.company }}</span>
        </div>
        <div>
          <label for="id_number" class="block text-sm font-medium text-gray-700">Beneficiary ID Number <span
              class="text-red-500">*</span></label>
          <input id="id_number" v-model="ffsForm.id_number" type="text"
            class="w-full px-3 py-2 mt-1 text-sm border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter ID Number" />
          <span v-if="errors.id_number" class="text-sm text-red-500">{{ errors.id_number }}</span>
        </div>
        <div class="flex items-end">
          <button type="button" :disabled="showLoader"
            class="w-full px-4 py-2 text-white rounded-lg bg-custom-blue hover:bg-blue-600" @click="searchBeneficiary">
            <span v-if="!showLoader">Search</span>
            <span v-else class="flex items-center justify-center">
              <svg class="w-5 h-5 mr-2 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none"
                viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" />
              </svg>
              Searching...
            </span>
          </button>
        </div>
      </div>
      <div v-if="beneficiaryFound" class="p-4 mt-4 rounded-lg bg-gray-50">
        <p class="text-green-600">Beneficiary found: {{ beneficiary.name }} (ID: {{ beneficiary.id_number }})</p>
        <p class="text-gray-600">Cover Amount: {{ beneficiary.cover_amount.toLocaleString('en-US', {
          minimumFractionDigits: 1
        }) }}</p>
        <div class="flex mt-4 space-x-4">
          <button type="button" class="px-4 py-2 text-white rounded-lg bg-custom-blue hover:bg-blue-600"
            @click="addPatientScheme">
            Add
          </button>
          <button type="button" class="px-4 py-2 text-gray-800 bg-gray-200 rounded-lg hover:bg-gray-300"
            @click="resetFFSForm">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Schemes Table -->
    <div v-if="patientSchemes.length" class="w-full mt-8 overflow-x-auto">
      <table class="w-full border border-collapse border-gray-300 table-auto">
        <thead>
          <tr>
            <th class="px-4 py-2 border">No</th>
            <th class="px-4 py-2 border">Payment Method</th>
            <th class="px-4 py-2 border">Details</th>
            <th class="px-4 py-2 border">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(scheme, index) in patientSchemes" :key="'scheme-' + index" class="text-center">
            <td class="px-4 py-2 border">{{ index + 1 }}</td>
            <td class="px-4 py-2 border">{{ scheme.payment_method }}</td>
            <td class="px-4 py-2 text-left border">
              <!-- MTIBA Insurance -->
              <div v-if="scheme.payment_method === 'Insurance' && scheme.details.reservation_id" class="space-y-1">
                <div class="flex items-center">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">
                    M-TIBA Insurance
                  </span>
                </div>
                <div class="space-y-1 text-xs text-gray-600">
                  <div><strong>Visit Code:</strong> {{ scheme.details.visit_code }}</div>
                  <div><strong>Beneficiary:</strong> {{ scheme.details.beneficiary_data?.beneficiary_name }}</div>
                  <div><strong>Policy:</strong> {{ scheme.details.beneficiary_data?.policy_number }}</div>
                  <div><strong>Coverage:</strong> {{ scheme.details.beneficiary_data?.currency }} {{
                    scheme.details.beneficiary_data?.amount?.toLocaleString() }}</div>
                  <div><strong>Status:</strong>
                    <span
                      :class="scheme.details.beneficiary_data?.status === 'ACTIVE' ? 'text-green-600 font-medium' : 'text-yellow-600'">
                      {{ scheme.details.beneficiary_data?.status }}
                    </span>
                  </div>
                </div>
              </div>
              <!-- SHA Insurance -->
              <div v-else-if="scheme.payment_method === 'Insurance' && scheme.details.sha_visit_claim_id"
                class="space-y-1">
                <div class="flex items-center">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-400 bg-orange-100 rounded-full">
                    SHA Insurance
                  </span>
                </div>
                <div class="space-y-1 text-xs text-gray-600">
                  <div><strong>CR Number:</strong> {{ scheme.details.cr_number }}</div>
                  <div><strong>Member:</strong> {{ scheme.details.member_name || scheme.details.eligibility_data?.fullName }}</div>
                  <div><strong>Visit:</strong> {{ scheme.details.visit_code }}</div>
                  <div v-if="scheme.details.eligibility_data?.copay" class="text-orange-600">
                    Copay: {{ scheme.details.eligibility_data.copay.currency }} {{
                      scheme.details.eligibility_data.copay.amount?.toLocaleString() }}
                  </div>
                </div>
              </div>
              <!-- FFS -->
              <div v-else-if="scheme.payment_method === 'FFS'" class="space-y-1">
                <div class="flex items-center">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                    <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4">
                      </path>
                    </svg>
                    Fee-for-Service
                  </span>
                </div>
                <div class="space-y-1 text-xs text-gray-600">
                  <div><strong>Company:</strong> {{ scheme.details.company_name }}</div>
                  <div><strong>Beneficiary:</strong> {{ scheme.details.beneficiary_name }}</div>
                  <div><strong>ID Number:</strong> {{ scheme.details.id_number }}</div>
                </div>
              </div>
              <div v-else class="flex items-center">
                <span
                  class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full">
                  <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z">
                    </path>
                  </svg>
                  {{ scheme.payment_method }} Payment
                </span>
              </div>
            </td>
            <td class="px-4 py-2 border">
              <button type="button" class="text-red-500 hover:underline"
                @click="removePatientScheme(index)">Remove</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
/* Add any additional styles if needed */
</style>